import PropTypes from "prop-types";
import React from "react";
import { DataTable, TableHeader, TableBody, TableRow, TableColumn } from "react-md/lib/DataTables";
import { FieldGroup, MSpinner } from "components/Shared";
import { Card, CardText, CardTitle, Cell, Grid } from "react-md";
import { dateFormatter, dateTimeToString } from "../Shared/Formatters";
import AppInsightsTracker from "components/Shared/AppInsightsTracker";
import PayPeriodDropdown from "components/User/PayPeriodDropdown.tsx";

class TimeLogs extends AppInsightsTracker {
    constructor(props) {
        super(props);
        this.displayName = "TimeLogs";
        this.navigatePayPeriod = this.navigatePayPeriod.bind(this);
        this.getHours = this.getHours.bind(this);
        this.state = {
            caseCount: 0,
            totalHours: 0,
            selectedPayPeriodIndex: props.selectedPayPeriodIndex ? props.selectedPayPeriodIndex : 0
        };
    }

    componentDidMount() {
        if (this.props.payPeriods.length === 0) {
            this.props.getPayPeriodList();
        } else {
            const { getCaseTimeLogByUserPayPeriod, payPeriods } = this.props;
            getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, payPeriods[this.state.selectedPayPeriodIndex].id);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { isFetchingPayPeriods, getCaseTimeLogByUserPayPeriod, payPeriods } = this.props;
        // get the time logs by the first payperiod that comes back.
        if (!isFetchingPayPeriods && prevProps.isFetchingPayPeriods) {
            getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, payPeriods[prevState.selectedPayPeriodIndex].id);
        }
    }

    navigatePayPeriod(newSelectedIndex) {
        this.setState({ selectedPayPeriodIndex: newSelectedIndex });
        if (this.props.callBackPayPeriod) this.props.callBackPayPeriod(newSelectedIndex);
        // refresh the list
        if (newSelectedIndex == null) {
            //  pending list
            this.props.getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, this.props.payPeriods[0].id);
        }
        else {
            // actual pay period
            this.props.getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, this.props.payPeriods[newSelectedIndex].id);
        }
    }

    getHours() {
        const { timeLogs } = this.props;
        if (timeLogs.length > 0) {
            return (timeLogs.map(timeLog => {
                return timeLog.timeMinutes;
            }).reduce((prev, curr) => {
                return prev + curr;
            }) / 60).toFixed(2);
        }
        return (0).toFixed(2);
    }

    render() {
        const {
            isFetchingTimeLogs,
            timeLogs,
            payPeriods,
            isFetchingPayPeriods
        } = this.props;

        const { selectedPayPeriodIndex } = this.state;

        if (isFetchingTimeLogs || isFetchingPayPeriods) {
            return <MSpinner />;
        }

        let payrollStatus = payPeriods[this.state.selectedPayPeriodIndex].closedDate == null ? "Pending" : dateTimeToString(payPeriods[this.state.selectedPayPeriodIndex].closedDate);

        let payPeriodValue = payPeriods[this.state.selectedPayPeriodIndex].fromToDisplay;
        let closedOnValue = payrollStatus.toString();
        let casesValue = timeLogs.length.toString();
        let totalHours = this.getHours();

        let fieldLabels = ["Pay Period ", "Closed on ", "Cases ", "Production Hours "];
        let fieldValues = [payPeriodValue, closedOnValue, casesValue, totalHours];

        if (this.props.fieldSet === SchedulingAssistantFieldSet) {
            fieldLabels = ["Pay Period ", "Closed on "];
            fieldValues = [payPeriodValue, closedOnValue];
        }

        const timeLogSummaryCard = () => {
            return (
                <Card className="card-unnested">
                    <CardTitle title="Time Log Summary" />
                    <CardText>
                        <PayPeriodDropdown
                            payPeriods={payPeriods}
                            selectedPayPeriodIndex={selectedPayPeriodIndex}
                            payPeriodChangeCallback={this.navigatePayPeriod}
                        />
                        <hr />
                        <FieldGroup
                            labels={fieldLabels}
                            values={fieldValues}
                        />
                    </CardText>
                </Card>
            );
        };

        const summaryAndTable = () => {
            return (
                <Grid className="case-header" >
                    <Cell size={4}>
                        {timeLogSummaryCard()}
                    </Cell>
                    <Cell size={12}>
                        <Card>
                            <DataTable plain>
                                <TableHeader>
                                    <TableRow>
                                        <TableColumn>Date</TableColumn>
                                        <TableColumn>Control #</TableColumn>
                                        <TableColumn>Address</TableColumn>
                                        <TableColumn>Survey Type</TableColumn>
                                        <TableColumn>Work Type</TableColumn>
                                        <TableColumn>Minutes</TableColumn>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {timeLogs.map((t, index) => (
                                        <TableRow key={"t" + index}>
                                            <TableColumn>{dateFormatter(t.createDate, "ddd MMM D YYYY")}</TableColumn>
                                            <TableColumn>{t.controlIdNumber}</TableColumn>
                                            <TableColumn>{t.surveyAddress}</TableColumn>
                                            <TableColumn>{t.surveyTypeName}</TableColumn>
                                            <TableColumn>{t.workTypeName}</TableColumn>
                                            <TableColumn>{t.timeMinutes} Minutes</TableColumn>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </DataTable>
                        </Card>
                    </Cell>
                </Grid>
            );
        };
        return (
            this.props.displayJustSummaryCard ? timeLogSummaryCard() : summaryAndTable()
        );
    }
}

export const SchedulingAssistantFieldSet = "SchedulingAssistant";

TimeLogs.propTypes = {
    isFetchingTimeLogs: PropTypes.bool.isRequired,
    timeLogs: PropTypes.array.isRequired,
    payPeriods: PropTypes.array.isRequired,
    isFetchingPayPeriods: PropTypes.bool.isRequired,
    getCaseTimeLogByUserPayPeriod: PropTypes.func.isRequired,
    getPayPeriodList: PropTypes.func.isRequired,
    displayJustSummaryCard: PropTypes.bool,
    callBackPayPeriod: PropTypes.func,
    selectedPayPeriodIndex: PropTypes.number,
    internetUserId: PropTypes.number,
    fieldSet: PropTypes.oneOf([SchedulingAssistantFieldSet]), // changes the default fields in the card. if you want to customize the fields for other displays, just add the string here in the array
};

TimeLogs.defaultProps = {
    displayJustSummaryCard: false,
};

export default TimeLogs;